import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  Loader2,
  Rocket,
  Sparkles,
  Star,
  Target,
  Trophy,
  Zap,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import AppSearchInput from "../shared/AppSearchInput";
import { BACKEND_URL } from "../../lib/api";

const API = BACKEND_URL;

const TRUST_POINTS = [
  { icon: Zap, label: "Instant — generated in seconds" },
  { icon: Trophy, label: "Benchmarks against top competitors" },
  { icon: Sparkles, label: "Quick wins identified automatically" },
  { icon: ShieldCheck, label: "100% free · no signup required" },
];

// Variable durations — feels organic instead of metronomic
const ANALYSIS_STEPS = [
  { label: "Connecting to app store…", detail: "Establishing secure connection", icon: Loader2, ms: 700 },
  { label: "Fetching app metadata…", detail: "Title, subtitle, category, rating", icon: Target, ms: 1100 },
  { label: "Analysing listing & keywords…", detail: "Scanning description for indexed terms", icon: Sparkles, ms: 1700 },
  { label: "Scoring screenshots & visuals…", detail: "Checking caption density & video presence", icon: Sparkles, ms: 1300 },
  { label: "Reading user reviews…", detail: "Sentiment & rating distribution", icon: Star, ms: 1500 },
  { label: "Comparing top competitors…", detail: "Top 3 in your category", icon: Trophy, ms: 1100 },
  { label: "Compiling your audit report…", detail: "Calculating scores & quick wins", icon: Rocket, ms: 800 },
];

export default function CTABanner() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [selectedApp, setSelectedApp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);

  const handleSelectApp = (app) => {
    setSelectedApp(app);
    setQuery(app.name);
  };

  const runAnimation = () => {
    return new Promise((resolve) => {
      const total = ANALYSIS_STEPS.reduce((a, s) => a + s.ms, 0);
      let elapsed = 0;
      let i = 0;
      const tick = () => {
        if (i >= ANALYSIS_STEPS.length) {
          setProgress(100);
          resolve();
          return;
        }
        setStep(i);
        const stepMs = ANALYSIS_STEPS[i].ms;
        const stepStart = elapsed;
        // Smoothly tick progress every 50ms within the step
        const subInt = setInterval(() => {
          const now = Date.now() - tickStart;
          const stepProgress = Math.min(1, (now - stepStart) / stepMs);
          const overall = ((stepStart + stepProgress * stepMs) / total) * 100;
          setProgress(overall);
        }, 50);
        const stepTimer = setTimeout(() => {
          clearInterval(subInt);
          elapsed += stepMs;
          i += 1;
          tick();
        }, stepMs);
        // Save handles for cleanup if needed
        runAnimation._cleanup = () => {
          clearInterval(subInt);
          clearTimeout(stepTimer);
        };
      };
      const tickStart = Date.now();
      tick();
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedApp) {
      toast.error("Please select your app from the search results.");
      return;
    }

    setLoading(true);
    setStep(0);
    setProgress(0);
    setDone(false);

    // Run API + animation in parallel — wait for both
    const apiPromise = fetch(`${API}/api/audit/start`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ url: selectedApp.url }),
    }).then(async (res) => {
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Could not fetch app data.");
      }
      return res.json();
    });

    try {
      const [data] = await Promise.all([apiPromise, runAnimation()]);
      // Brief "Report ready" celebration
      setDone(true);
      await new Promise((r) => setTimeout(r, 900));
      navigate("/audit-report", { state: { preview: data } });
    } catch (err) {
      runAnimation._cleanup?.();
      setLoading(false);
      setStep(0);
      setProgress(0);
      setDone(false);
      toast.error(err.message || "Something went wrong. Try again.");
    }
  };

  return (
    <section
      id="audit"
      className="relative py-20 md:py-28 bg-charcoal text-white overflow-hidden"
      data-testid="cta-banner-section"
    >
      <div className="absolute inset-0 bg-grid-pattern-dark opacity-50 pointer-events-none" />
      <div
        className="absolute -right-32 -top-32 w-[480px] h-[480px] rounded-full bg-electric/20 blur-3xl pointer-events-none"
        aria-hidden
      />

      <div className="container relative grid lg:grid-cols-12 gap-10 items-start">
        {/* Left copy */}
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <p className="text-overline text-electric mb-4">09 — App Audit Report</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-balance">
            Get a{" "}
            <span className="relative inline-block">
              <span className="relative z-10">free ASO audit</span>
              <span className="absolute inset-x-0 bottom-1 h-3 bg-electric/30 -z-0" />
            </span>{" "}
            built from this exact playbook.
          </h2>
          <p className="mt-5 text-lg text-zinc-300 max-w-md leading-relaxed">
            Search your app on the App Store or Google Play. We'll analyse
            your listing, ratings, visuals and freshness — and show you
            exactly what's holding back your downloads.
          </p>
          <ul className="mt-8 space-y-3">
            {TRUST_POINTS.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-3 text-sm text-zinc-300">
                <span className="w-7 h-7 grid place-items-center bg-electric/10 border border-electric/30 flex-shrink-0">
                  <Icon className="w-3.5 h-3.5 text-electric" />
                </span>
                {label}
              </li>
            ))}
          </ul>
        </div>

        {/* Right card */}
        <div className="lg:col-span-7">
          <div className="bg-white text-charcoal border border-charcoal shadow-[8px_8px_0_0_#2196F3]">
            <div className="bg-electric px-7 py-4 flex items-center gap-2.5">
              <Sparkles className="w-4 h-4 text-white" />
              <p className="text-sm font-bold text-white tracking-wide uppercase">
                Live App Audit · Free · Instant
              </p>
            </div>

            <div className="px-7 py-7">
              <AnimatePresence mode="wait">
                {!loading ? (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onSubmit={handleSubmit}
                    noValidate
                  >
                    <h3 className="font-display text-2xl md:text-3xl font-extrabold text-charcoal leading-tight">
                      Search your app to start.
                    </h3>
                    <p className="mt-2 text-sm text-zinc-600 leading-relaxed">
                      Live results from the Apple App Store and Google Play.
                      Select your app and get your report in seconds.
                    </p>

                    <div className="mt-6">
                      <span className="text-overline text-electric block mb-2">
                        App Name
                      </span>
                      <AppSearchInput
                        value={query}
                        onChange={(val) => {
                          setQuery(val);
                          if (selectedApp && val !== selectedApp.name) {
                            setSelectedApp(null);
                          }
                        }}
                        onSelectApp={handleSelectApp}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={!selectedApp}
                      data-testid="audit-submit"
                      className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-electric text-white px-6 py-4 font-bold text-sm hover:bg-electric-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Get My Free Audit <ArrowRight className="w-4 h-4" />
                    </button>

                    <div className="mt-5 pt-5 border-t border-zinc-100 grid grid-cols-3 gap-3 text-center">
                      {["Health score 0–100", "Top-3 competitors", "Quick wins"].map((t) => (
                        <div key={t}>
                          <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Included</p>
                          <p className="text-xs font-semibold text-charcoal mt-0.5">{t}</p>
                        </div>
                      ))}
                    </div>
                  </motion.form>
                ) : (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    {/* App preview header */}
                    {selectedApp?.icon && (
                      <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-3 mb-5"
                      >
                        <img
                          src={selectedApp.icon}
                          alt={selectedApp.name}
                          className="w-12 h-12 rounded-xl border border-zinc-200 flex-shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-charcoal text-sm truncate">{selectedApp.name}</p>
                          <p className="text-xs text-zinc-500 truncate">
                            {selectedApp.developer} · {selectedApp.platform === "ios" ? "iOS" : "Android"}
                          </p>
                        </div>
                        <span
                          className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 ${
                            selectedApp.platform === "ios"
                              ? "bg-blue-50 text-blue-600 border border-blue-200"
                              : "bg-green-50 text-green-600 border border-green-200"
                          }`}
                        >
                          {selectedApp.platform === "ios" ? "iOS" : "Android"}
                        </span>
                      </motion.div>
                    )}

                    {/* Top progress bar */}
                    <div className="mb-5">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-display text-base font-extrabold text-charcoal">
                          {done ? "Report ready!" : "Analysing your app…"}
                        </h3>
                        <motion.span
                          key={Math.round(progress)}
                          initial={{ opacity: 0.6 }}
                          animate={{ opacity: 1 }}
                          className="text-xs font-black tabular-nums text-electric"
                        >
                          {Math.round(progress)}%
                        </motion.span>
                      </div>
                      <div className="relative h-1.5 bg-zinc-100 overflow-hidden">
                        <motion.div
                          className="absolute inset-y-0 left-0 bg-electric"
                          style={{ width: `${progress}%` }}
                          transition={{ duration: 0.05, ease: "linear" }}
                        />
                        {/* Shimmer sweep on progress bar */}
                        <motion.div
                          className="absolute inset-y-0 w-20 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                          initial={{ x: "-100%" }}
                          animate={{ x: "600%" }}
                          transition={{ duration: 1.8, repeat: Infinity, ease: "linear" }}
                        />
                      </div>
                    </div>

                    {/* Steps */}
                    <ul className="space-y-2.5">
                      {ANALYSIS_STEPS.map((s, idx) => {
                        const Icon = s.icon;
                        const isDone = idx < step || done;
                        const isActive = idx === step && !done;
                        return (
                          <motion.li
                            key={s.label}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: isActive || isDone ? 1 : 0.32, y: 0 }}
                            transition={{ duration: 0.35 }}
                            className="relative flex items-center gap-3 text-sm overflow-hidden"
                          >
                            <motion.span
                              animate={{ scale: isActive ? [1, 1.08, 1] : 1 }}
                              transition={{
                                duration: 1.2,
                                repeat: isActive ? Infinity : 0,
                                ease: "easeInOut",
                              }}
                              className={`w-7 h-7 grid place-items-center flex-shrink-0 border ${
                                isDone
                                  ? "bg-electric border-electric"
                                  : isActive
                                    ? "bg-electric/10 border-electric/40"
                                    : "bg-zinc-50 border-zinc-200"
                              }`}
                            >
                              {isDone ? (
                                <motion.span
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{ type: "spring", stiffness: 400 }}
                                >
                                  <CheckCircle2 className="w-4 h-4 text-white" />
                                </motion.span>
                              ) : isActive ? (
                                <Loader2 className="w-4 h-4 text-electric animate-spin" />
                              ) : (
                                <Icon className="w-3.5 h-3.5 text-zinc-400" />
                              )}
                            </motion.span>

                            <div className="flex-1 min-w-0">
                              <p
                                className={
                                  isDone
                                    ? "text-zinc-400 text-xs"
                                    : isActive
                                      ? "text-charcoal font-semibold text-sm"
                                      : "text-zinc-400 text-sm"
                                }
                              >
                                {s.label}
                              </p>
                              {isActive && (
                                <motion.p
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  className="text-[11px] text-zinc-500 mt-0.5"
                                >
                                  {s.detail}
                                </motion.p>
                              )}
                            </div>

                            {/* Shimmer sweep over the active row */}
                            {isActive && (
                              <motion.div
                                className="absolute inset-0 pointer-events-none bg-gradient-to-r from-transparent via-electric/10 to-transparent"
                                initial={{ x: "-100%" }}
                                animate={{ x: "100%" }}
                                transition={{
                                  duration: 1.5,
                                  repeat: Infinity,
                                  ease: "easeInOut",
                                }}
                                style={{ width: "60%" }}
                              />
                            )}
                          </motion.li>
                        );
                      })}
                    </ul>

                    {/* Done celebration */}
                    <AnimatePresence>
                      {done && (
                        <motion.div
                          initial={{ opacity: 0, y: 8, scale: 0.96 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ type: "spring", stiffness: 320, damping: 20 }}
                          className="mt-5 flex items-center gap-3 bg-electric/10 border border-electric/30 px-4 py-3"
                        >
                          <motion.span
                            initial={{ scale: 0, rotate: -90 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ type: "spring", stiffness: 400 }}
                            className="w-8 h-8 grid place-items-center bg-electric flex-shrink-0"
                          >
                            <CheckCircle2 className="w-5 h-5 text-white" />
                          </motion.span>
                          <div className="flex-1">
                            <p className="text-sm font-bold text-charcoal">Report ready</p>
                            <p className="text-xs text-zinc-500">Redirecting to your audit…</p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {!done && (
                      <p className="mt-5 text-[11px] text-zinc-400 flex items-center gap-1.5">
                        <span className="inline-block w-1 h-1 bg-electric rounded-full animate-pulse" />
                        Pulling live data from the App Store & Google Play…
                      </p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
